<template>
  <v-container fluid :class="{'pa-0 fill-height bg-background': !isMobile, 'just-bg': true}" style="background: white !important;">
    <!-- Mobile Header -->
    <!-- <div v-if="isMobile" class="mobile-header mx-auto mb-0">
      <v-avatar size="80" class="mb-4">
        <v-img :src="logo" :aspect-ratio="1"></v-img>
      </v-avatar>
      <h1 class="text-h4 font-weight-bold mb-2 text-center">{{ stateGlobal.institutionName }}</h1>
      <p class="text-subtitle-1 text-center text-medium-emphasis">Welcome to your student portal</p>
    </div> -->
    <v-row no-gutters :class="{'fill-height': !isMobile}">
      <!-- Main Content Area -->
      <v-col cols="12" md="7" class="d-flex align-center justify-center">
        <v-card :width="isMobile ? '95%' : 500" elevation="8" class="mx-auto rounded-xl pa-6"
          :class="{ 'ma-4': isMobile }">
          <!-- Login Form Area -->
          <div v-if="!showForgotPassword">
            <div v-if="!isMfa">
              <!-- Header -->
              <v-card-title class="text-h4 font-weight-bold mb-2" style="color: #1a237e">
                Welcome Back
              </v-card-title>
              <v-card-subtitle class="text-subtitle-1 mb-1">
                {{ isMobile ? 'Sign in to access your account' : 'Please login below to continue' }}
              </v-card-subtitle>

              <!-- Alerts -->
              <v-alert v-if="usingOldReg" type="info" variant="tonal" closable class="mb-4">{{ newRegMessage
                }}</v-alert>

              <!-- Login Form -->
              <div v-if="isUsernamePasswordAuth" class="pa-4">
                <v-form @submit.prevent="login">
                  <x-text-field v-model="formData.username" label="User ID" :rules="[v => !!v || 'User ID is required']"
                    prepend-inner-icon="mdi-account" outlined class="mb-2"></x-text-field>

                  <x-text-field v-model="formData.password" :type="showPassword ? 'text' : 'password'" label="Password"
                    :rules="[v => !!v || 'Password is required']" prepend-inner-icon="mdi-lock"
                    :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'" @click:append="showPassword = !showPassword"
                    outlined class="mb-4" @keyup.enter="login"></x-text-field>

                  <!-- Action Links -->
                  <div class="d-flex justify-space-between align-center mb-6">
                    <a @click.prevent="showForgotPassword = true" class="text-decoration-none text-primary">
                      Forgot Password?
                    </a>
                    <a v-if="isMediumAndUp" @click.prevent="switchMode" class="text-decoration-none text-primary">
                      Use Biometric
                    </a>
                  </div>

                  <!-- Login Button -->
                  <x-button block color="primary" size="large" rounded height="44" :loading="isLoading" @click="login"
                    class="mb-6">
                    Sign In
                  </x-button>

                  <!-- Registration Link -->
                  <div class="text-center">
                    <span class="text-medium-emphasis">Not a student? </span>
                    <a href="/application" class="text-decoration-none text-primary font-weight-medium">
                      Application Portal
                    </a>
                  </div>
                  <div class="text-center mt-2">
                    <a href="/convocation" class="text-decoration-none text-primary font-weight-medium">
                      Convocation Portal
                    </a>
                  </div>
                </v-form>
              </div>

              <!-- Biometric Login -->
              <div v-else class="pa-4">
                <v-alert :type="deviceConnected ? 'success' : 'error'" variant="tonal" class="mb-4" text>
                  {{ deviceConnected ? 'Biometric device connected' : 'No biometric device detected' }}
                </v-alert>

                <x-text-field v-model="formData.username" label="User ID" outlined prepend-inner-icon="mdi-account"
                  class="mb-4"></x-text-field>

                <v-alert type="info" variant="tonal" icon="mdi-fingerprint" class="mb-4" text>
                  Place your finger on the scanner when ready
                </v-alert>

                <x-button block color="secondary" @click="switchMode" class="mt-4" rounded>
                  Switch to Password Login
                </x-button>
              </div>
            </div>

            <!-- MFA Section -->
            <div v-else>
              <v-card-title class="text-h5 mb-4">Two-Factor Authentication</v-card-title>

              <v-alert type="error" variant="tonal" class="mb-4">
                Extra verification required to proceed
              </v-alert>

              <x-text-field v-if="!usingRecovery" v-model="mfa.code" label="Authentication Code" outlined
                class="mb-4"></x-text-field>

              <x-text-field v-else v-model="mfa.recovery" label="Recovery Code" outlined class="mb-4"></x-text-field>

              <v-switch v-model="usingRecovery" label="Use Recovery Code" class="mb-4"></v-switch>

              <x-button block color="primary" size="large" :loading="isLoading"
                :disabled="(usingRecovery ? !mfa.recovery : !mfa.code) || loading" @click="verifyCode">
                Verify
              </x-button>
            </div>
          </div>

          <!-- Forgot Password -->
          <forgot-password v-else :global-config="stateGlobal" @close="showForgotPassword = false"></forgot-password>
        </v-card>
      </v-col>

      <!-- Improved Right Side Banner -->
      <v-col md="5" class="d-none d-md-flex">
        <div class="right-side-banner">
          <div class="banner-content">
            <!-- Top Section with Logo and Name -->
            <div class="banner-header">
              <v-avatar size="80" class="institution-logo">
                <v-img :src="logo" :aspect-ratio="1" class="white--text"></v-img>
              </v-avatar>
              <h2 class="institution-name">{{ stateGlobal.institutionName }}</h2>
            </div>

            <!-- Middle Section with Welcome Message -->
            <div class="banner-message">
              <div class="message-box">
                <v-icon color="white" size="36" class="mb-4">mdi-school</v-icon>
                <p class="welcome-text">
                  Welcome to the {{ stateGlobal.institutionName }} portal
                </p>
                <p class="info-text">
                  Access to certain services is restricted to authenticated users only.
                </p>
              </div>
            </div>

            <!-- Bottom Section with Motto -->
            <div class="banner-footer">
              <div class="motto-box">
                <v-icon color="white" size="24" class="mb-2">mdi-format-quote-open</v-icon>
                <p class="motto-text">{{ stateGlobal.motto }}</p>
              </div>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Messages -->
    <message-box :show="messageShow" :message="message" :type="messageType"
      @close-message="messageShow = false"></message-box>

    <!-- Loading -->
    <loading-dialog :show="loading"></loading-dialog>
  </v-container>
</template>

<script>

import ErrorResolver from "@/shared/ErrorResolver";
import Api from "@/main/api";
import MessageBox from "@ui/components/MessageBox";
import Roles from "@/shared/Roles";
import LoginUtil from "../../LoginUtil";
import AuthenticationMode from "@/shared/constants/AuthenticationMode";
import LoadingDialog from "@ui/components/LoadingDialog";
import ForgotPassword from "./ForgotPassword";
import Defaults from "@/shared/Defaults";
import Links from "@/shared/constants/Links";
import FingerprintSdk from "@/utils/FingerprintSDK";
import XTextField from '@ui/widgets/XTextField';
import XButton from '@ui/widgets/XButton.vue';

export default {
  name: 'AuthV2',
  components: { ForgotPassword, LoadingDialog, MessageBox, XTextField, XButton },
  data: () => ({
    isLoading: false,
    loading: false,
    hasError: false,
    errorMsg: "",
    mfaMsg: "",
    mfaSent: false,
    messageType: '',
    message: '',
    messageShow: false,
    usingOldReg: false,
    newRegMessage: '',
    changeCourseMessage: '',
    authMode: 0,
    reader: null,
    deviceConnected: false,
    showForgotPassword: false,
    showPassword: false,
    logo: '',
    stateGlobal: {},
    formData: {
      username: "",
      password: ""
    },

    mfa: {
      userId: '',
      code: '',
      recovery: null
    },
    mfas: [],
    usingRecovery: false,

    isMfa: false, // for multi factor auth

  }),

  methods: {
    login() {
      if (this.formData.password === undefined || this.formData.password === '' || this.formData.username === undefined || this.formData.username === '')
        return
      // start loading
      this.isLoading = true;
      this.usingOldReg = false;

      Api.auth.login(this.formData).then(res => {

        this.hasError = false;
        // if using old reg no.
        if (res.data.old) {
          if (res.data.applicant) { //
            this.usingOldReg = true;
            this.newRegMessage = "Use the username: " + res.data.applicant + " as you new username";
          } else if (res.data.message) { // change of programme
            this.usingOldReg = true;
            this.newRegMessage = "Use the username: " + res.data.message + " as you new username";
          } else if (res.data.userId) { //
            this.usingOldReg = true;
            this.newRegMessage = "Use the following registration number: " + res.data.userId + " as you new username";
            // this.newRegMessage = "Login to the institutional email account: " + res.data.userId + ' with the password: 12345678 for your student ID'
          }
        }
        // if multi factor enabled
        else if (res.data.mfa) {
          this.isMfa = true
          this.mfa.userId = res.data.userId
        } else {
          // save to cookie
          LoginUtil.setAccessToken(res.data.jwt);
          LoginUtil.setRefreshToken(res.data.refreshToken);
          if (Roles.contains(res.data.roles, Roles.Admin)) {
            window.location.href = '/main';
          } else if (Roles.contains(res.data.roles, Roles.Student) || Roles.contains(res.data.roles, Roles.Applicant)) {
            window.location.href = '/student';
          } else {
            window.location.href = '/officer';
          }
        }

      }).catch(err => {
        this.hasError = true
        ErrorResolver.checkErrors(this, err)
      }).finally(() => {
        this.isLoading = false;
      })
    },

    verifyCode() {
      this.isLoading = true
      this.mfa.code = this.usingRecovery ? this.mfa.recovery : this.mfa.code
      Api.auth.verifyMfa(this.mfa, this.usingRecovery)
        .then(res => {
          // save to cookie
          LoginUtil.setAccessToken(res.data.jwt)
          LoginUtil.setRefreshToken(res.data.refreshToken);

          if (Roles.contains(res.data.roles, Roles.Admin) || Roles.contains(res.data.roles, Roles.SuperAdmin)) {
            window.location.href = '/main';
          } else if (Roles.contains(res.data.roles, Roles.Student)) {
            window.location.href = '/student';
          } else {
            window.location.href = '/officer';
          }
        }).catch(err => {
          ErrorResolver.checkErrors(this, err)
        }).finally(() => {
          this.isLoading = false
        })
    },

    switchMode() {
      if (this.authMode === AuthenticationMode.USERNAME_PASSWORD) {
        this.authMode = AuthenticationMode.BIOMETRIC
        this.initializeDevice()
      } else {
        this.closeScanner()
        this.authMode = AuthenticationMode.USERNAME_PASSWORD
      }
    },

    closeScanner() {
      if (this.reader != null) {
        // this.reader.stopAcquisition();
        // this.reader.off();
        // this.connected = false
      }
    },

    onDeviceConnected(event) {
      console.log("Hey")
      this.deviceConnected = true
    },

    onDeviceDisconnected(event) {
      this.deviceConnected = false
    },

    onReaderError(event) {
      this.deviceConnected = false
    },

    onQuality(event) {

    },

    onSampleAcquired(event) {
      let samples = JSON.parse(event.samples);
      this.formData.password = this.reader.convertSample(samples[0]) // from base64url to base64
      if (!this.loading && this.formData.username !== '' && this.formData.username != null && this.authMode === AuthenticationMode.BIOMETRIC) {
        this.loading = true
        Api.auth.bioAuth(this.formData)
          .then(res => {
            if (res.data.success) {
              this.message = 'Verified'
              this.messageType = 'success'
              this.messageShow = true
              // save to cookie
              LoginUtil.setCookie("ctoken", res.data.details.jwt, 2);
              if (Roles.contains(res.data.details.roles, Roles.Admin)) {
                window.location.href = '/main';
              } else if (Roles.contains(res.data.details.roles, Roles.Student)) {
                window.location.href = '/student';
              } else {
                window.location.href = '/officer';
              }
            } else {
              this.message = 'Verification failed'
              this.messageType = 'error'
              this.messageShow = true
              this.loading = false
            }
          }).catch(err => {
            ErrorResolver.checkErrors(this, err)
            this.loading = false
          })
      }
    },

    initializeDevice() {
      // initialize finger print object
      this.reader = new FingerprintSdk(
        this.onDeviceConnected,
        this.onDeviceDisconnected,
        this.onSampleAcquired,
        this.onQuality,
        this.onReaderError
      );

      // start acquisition
      this.reader.startCapture()
    },

    fetchGlobal() {

      Api.global.fetch().then((res) => {
        this.stateGlobal = res.data;
        this.logo = Defaults.api + Links.global.logo;
      }).catch((err) => ErrorResolver.checkErrors(this, err))
        .finally(() => this.loading = false)
    },
  },



  computed: {
    isUsernamePasswordAuth: function () {
      return this.authMode === AuthenticationMode.USERNAME_PASSWORD
    },

    isMobile: function () {
      return this.$vuetify.breakpoint.smAndDown
    },

    isMediumAndUp: function () {
      return this.$vuetify.breakpoint.mdAndUp
    },
  },

  created() {
    this.fetchGlobal()
  }
}
</script>
<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Open+Sans:wght@300&display=swap');

/* Mobile Animations and Transitions */
@keyframes slideInUp {
  from {
    transform: translateY(100%);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.05);
  }

  100% {
    transform: scale(1);
  }
}

@keyframes shake {

  0%,
  100% {
    transform: translateX(0);
  }

  25% {
    transform: translateX(-5px);
  }

  75% {
    transform: translateX(5px);
  }
}

/* Base styles with mobile-first approach */
.just-bg {
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
}
.bg-background {
  
  min-height: 100vh;
  display: flex;
  align-items: center;
}

/* Mobile Header Styles */
.mobile-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 1rem;
  animation: fadeIn 0.5s ease-out;
}

.mobile-header .v-avatar {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 3px solid white;
  animation: fadeInUp 0.8s ease-out;
}

.mobile-header h1 {
  color: var(--v-primary-darken1);
  animation: fadeInUp 0.8s ease-out 0.1s;
  opacity: 0;
  animation-fill-mode: forwards;
}

.mobile-header p {
  animation: fadeInUp 0.8s ease-out 0.2s;
  opacity: 0;
  animation-fill-mode: forwards;
}

/* Mobile Card Styles */
.v-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: slideInUp 0.5s ease-out;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95);
}

@media (max-width: 600px) {
  .v-card {
    margin: 0 !important;
    border-radius: 20px 20px 0 0 !important;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    max-height: 90vh;
    overflow-y: auto;
    width: 100% !important;
  }
}

/* Form Field Animations */
.v-text-field {
  transition: all 0.3s ease;
  animation: fadeIn 0.5s ease-out;
}

.v-text-field:focus-within {
  transform: translateY(-2px);
}

/* Button Animations */
.v-btn {
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.v-btn::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  transition: width 0.3s ease, height 0.3s ease;
}

.v-btn:active::after {
  width: 200%;
  height: 200%;
}

/* Loading Animation */
.v-progress-circular {
  animation: pulse 1.5s infinite ease-in-out;
}

/* Error State Animation */
.v-alert[type="error"] {
  animation: shake 0.5s ease-in-out;
}

/* Touch Feedback */
.v-card,
.v-btn,
.v-text-field {
  -webkit-tap-highlight-color: transparent;
}

/* Mobile Form Group Spacing */
@media (max-width: 600px) {
  .form-group {
    margin-bottom: 16px;
  }

  .v-text-field {
    margin-bottom: 12px;
  }

  .v-card-title {
    font-size: 24px !important;
  }

  .v-card-subtitle {
    font-size: 16px !important;
  }
}

/* Base styles */
.bg-background {
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
}

.fill-height {
  height: 100vh;
}

/* Right Side Banner Base */
.right-side-banner {
  position: relative;
  height: 100%;
  width: 100%;
  background: linear-gradient(135deg, var(--v-primary-darken3, #016b4b) 0%, var(--v-primary-lighten1, #016b4b) 100%);
  overflow: hidden;
}

.banner-content {
  position: relative;
  height: 100%;
  padding: clamp(1.5rem, 3vw, 3rem);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  z-index: 2;
  gap: clamp(1rem, 2vw, 2rem);
}

/* Banner Header - Top Section */
.banner-header {
  display: flex;
  align-items: center;
  gap: clamp(0.75rem, 1.5vw, 1.5rem);
  padding: clamp(0.5rem, 1vw, 1rem);
  background: rgba(255, 255, 255, 0.1);
  border-radius: clamp(8px, 1vw, 16px);
  backdrop-filter: blur(10px);
  transform-origin: top;
  transition: all 0.3s ease;
}

.institution-logo {
  width: clamp(40px, 6vw, 80px) !important;
  height: clamp(40px, 6vw, 80px) !important;
  border: clamp(2px, 0.4vw, 4px) solid rgba(255, 255, 255, 0.9);
}

.institution-name {
  color: white;
  font-size: clamp(1rem, 2vw, 1.75rem);
  font-weight: 600;
  line-height: 1.3;
  text-transform: uppercase;
  margin: 0;
}

/* Banner Message - Middle Section */
.banner-message {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  /* Important for flex shrinking */
}

.message-box {
  background: rgba(255, 255, 255, 0.1);
  border-radius: clamp(8px, 1vw, 16px);
  padding: clamp(1rem, 2vw, 2rem);
  backdrop-filter: blur(10px);
  text-align: center;
  width: clamp(200px, 80%, 400px);
  transform-origin: center;
  transition: all 0.3s ease;
}

.welcome-text {
  color: white;
  font-size: clamp(1rem, 1.5vw, 1.5rem);
  font-weight: 500;
  margin-bottom: clamp(0.5rem, 1vw, 1rem);
  line-height: 1.4;
}

.info-text {
  color: rgba(255, 255, 255, 0.9);
  font-size: clamp(0.875rem, 1vw, 1.1rem);
  line-height: 1.6;
}

/* Banner Footer - Bottom Section */
.banner-footer {
  padding: clamp(0.5rem, 1vw, 1rem);
}

.motto-box {
  background: rgba(255, 255, 255, 0.1);
  border-radius: clamp(8px, 1vw, 16px);
  padding: clamp(1rem, 1.5vw, 1.5rem);
  backdrop-filter: blur(10px);
  text-align: center;
  transform-origin: bottom;
  transition: all 0.3s ease;
}

.motto-text {
  color: white;
  font-size: clamp(0.875rem, 1.25vw, 1.25rem);
  font-style: italic;
  margin: 0;
  line-height: 1.6;
}

/* Icon Scaling */
.banner-message .v-icon {
  font-size: clamp(24px, 3vw, 36px) !important;
  margin-bottom: clamp(0.5rem, 1vw, 1rem);
}

.motto-box .v-icon {
  font-size: clamp(18px, 2vw, 24px) !important;
  margin-bottom: clamp(0.25rem, 0.5vw, 0.5rem);
}

/* Background Pattern */
.right-side-banner::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  /* background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E") center/clamp(30px, 5vw, 60px); */
  opacity: 0.1;
  background-image: url("../../../resources/login-bg.jpg");
  background-size: cover;
}

/* Responsive Breakpoints */
@media (max-width: 1200px) {
  .banner-content {
    padding: 1.5rem;
  }

  .message-box {
    width: 90%;
  }
}

@media (max-width: 960px) {
  .banner-header {
    padding: 0.75rem;
  }

  .message-box {
    padding: 1.5rem;
  }

  .motto-box {
    padding: 1rem;
  }
}

/* Animation */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.banner-header,
.message-box,
.motto-box {
  animation: fadeInUp 0.8s ease-out;
}

/* Smooth transitions */
.v-card {
  transition: transform 0.3s ease;
}

.v-card:hover {
  transform: translateY(-2px);
}

.x-button {
  transition: all 0.3s ease;
}

/* Logo */
.banner-header {
  display: flex;
  align-items: center;
  gap: clamp(0.75rem, 1.5vw, 1.5rem);
  padding: clamp(0.5rem, 1vw, 1rem);
  background: rgba(255, 255, 255, 0.1);
  border-radius: clamp(8px, 1vw, 16px);
  backdrop-filter: blur(10px);
}

/* Logo Container */
.institution-logo {
  min-width: clamp(40px, 6vw, 80px);
  min-height: clamp(40px, 6vw, 80px);
  width: clamp(40px, 6vw, 80px) !important;
  height: clamp(40px, 6vw, 80px) !important;
  position: relative;
  border-radius: 50% !important;
  background: rgba(255, 255, 255, 0.1);
  padding: 0 !important;
  overflow: hidden;
}

/* Logo Border Effect */
.institution-logo::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  /* border: clamp(2px, 0.4vw, 4px) solid rgba(255, 255, 255, 0.9); */
  border-radius: 50%;
  z-index: 2;
  pointer-events: none;
}

/* Logo Image */
.institution-logo .v-image {
  width: 100% !important;
  height: 100% !important;
  border-radius: 50% !important;
}

/* Ensure image maintains aspect ratio */
.institution-logo :deep(.v-image__image) {
  background-size: cover;
  background-position: center;
}

/* Optional: Add subtle shadow */
.institution-logo {
  box-shadow: 0 clamp(2px, 0.4vw, 4px) clamp(8px, 1vw, 12px) rgba(0, 0, 0, 0.2);
}

/* Optional: Add subtle scaling animation on hover */
.institution-logo {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.institution-logo:hover {
  transform: scale(1.05);
  box-shadow: 0 clamp(4px, 0.6vw, 8px) clamp(12px, 1.5vw, 20px) rgba(0, 0, 0, 0.25);
}
</style>